import { Box, Link, MenuItem, Skeleton } from '@mui/material';
import { FC, MouseEvent, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectGetDefectsForSelectorStatus,
    selectGetDefectsForSelectorError,
    selectDefectsForSelector,
    selectDefectsForSelectorCount,
} from '../store/selectors';
import { getDefectsForSelectorRequest, getDefectsForSelectorReset, resetArrayDefectsForSelector } from '../store';
import { RequestStatusEnum } from '@shared/types';
import { usePrevious } from '@shared/hooks/usePrevious';
import { useListState } from '@shared/hooks/useListState';
import useHandleScroll from '@shared/hooks/useHandleScroll';
import UtmsSearchTextField from '@shared/components/ui/UtmsSearchTextField';
import HelperText from '@shared/components/others/HelperText/HelperText';
import ModalErrorContainer from '@shared/components/modals/ModalErrorContainer';
import { Defect } from '../types';
import { useDefectsChunk } from '../hooks/useDefectsChunk';
import ComponentWithChunks from '@shared/components/others/ComponentWithChunk/ComponentWithChunk';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import UtmsChip from '@shared/components/ui/UtmsChip';
import { DEFECT_STATUS_COLOR, DEFECT_TYPE_COLOR } from '../constants';
import { useNavigate } from 'react-router-dom';
import { getDefectLinkById } from '@shared/utils/utils';
import { convertToTranslationKey, toTitleCase } from '@shared/utils/utilsOld';

interface DefectSelectorMenuProps {
    open: boolean;
    onClose: () => void;
    onChange?: (defect: Defect) => void;
    readOnly?: boolean;
    defects?: Defect[];
}

const SKELETONS_COUNT = 6;

const DefectSelectorMenu = (props: DefectSelectorMenuProps) => (
    <ComponentWithChunks
        featuresArray={['defects']}
        hooksArray={[useDefectsChunk]}
        Component={DefectSelectorMenuComponent}
        {...props}
    />
);

const DefectSelectorMenuComponent: FC<DefectSelectorMenuProps> = ({ open, onChange, onClose, readOnly, defects }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const defectsForSelector = useSelector(selectDefectsForSelector);
    const defectsForSelectorCount = useSelector(selectDefectsForSelectorCount);
    const getDefectsForSelectorStatus = useSelector(selectGetDefectsForSelectorStatus);
    const isLoading = !Boolean(defects) && getDefectsForSelectorStatus === RequestStatusEnum.Loading;
    const isResettingRef = useRef(false);
    const { pagination, setPagination, globalFilter, setGlobalFilter } = useListState(
        'defectSelector',
        Boolean(defects),
        getDefectsForSelectorRequest,
        { disableUrlParams: true },
    );
    const prevGlobalFilter = usePrevious(globalFilter);
    const { pageIndex, pageSize } = pagination;
    const defectsView = defects
        ? defects.filter((defect) => {
              const query = globalFilter.toLowerCase();
              if (!query) return true;
              return (
                  String(defect.pk).includes(query) ||
                  defect.name.toLowerCase().includes(query) ||
                  defect.jiraIssueKey?.toLowerCase().includes(query)
              );
          })
        : defectsForSelector;
    const defectsViewCount = defects ? defectsView.length : defectsForSelectorCount;
    const allFetched = (pageIndex + 1) * pageSize >= defectsViewCount;
    const navigate = useNavigate();

    const handleScroll = useHandleScroll({
        isLoading,
        setPagination,
        allItemsFetched: allFetched,
    });

    const handleSelectDefect = (defect: Defect) => (_e: MouseEvent<HTMLElement>) => {
        onChange && onChange(defect);
        onClose();
    };

    useEffect(() => {
        if (prevGlobalFilter !== undefined && globalFilter !== prevGlobalFilter) {
            isResettingRef.current = true;
            dispatch(resetArrayDefectsForSelector());
        }
    }, [dispatch, globalFilter, prevGlobalFilter]);

    useEffect(() => {
        isResettingRef.current = true;
        dispatch(resetArrayDefectsForSelector());
    }, [dispatch, open]);

    useEffect(() => {
        if (!isLoading) {
            isResettingRef.current = false;
        }
    }, [isLoading]);

    const defectItem = useCallback(
        (defect: Defect) => (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <UtmsChip label={t(defect.status.toLowerCase())} color={DEFECT_STATUS_COLOR[defect.status]} />
                <UtmsChip
                    label={toTitleCase(t(convertToTranslationKey(defect.type)))}
                    color={DEFECT_TYPE_COLOR[defect.type]}
                />
                <Box sx={{ display: 'flex', gap: 1 }}>
                    #{defect.pk}
                    {defect.jiraIssueKey && (
                        <Link
                            href={defect.jiraIssueUrl}
                            onClick={(e) => e.stopPropagation()}
                            underline="hover"
                            target="_blank"
                            sx={{ cursor: 'pointer' }}
                        >
                            {defect.jiraIssueKey}
                        </Link>
                    )}
                    {readOnly ? (
                        <OverflowTooltip title={defect.name} maxWidth={300}>
                            <Link
                                underline="hover"
                                onClick={(e) => {
                                    e.preventDefault();
                                    navigate(getDefectLinkById(defect.pk));
                                }}
                                sx={{ cursor: 'pointer' }}
                                href={getDefectLinkById(defect.pk)}
                            >
                                {defect.name}
                            </Link>
                        </OverflowTooltip>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <OverflowTooltip title={defect.name} maxWidth={300}>
                                {defect.name}
                            </OverflowTooltip>
                        </Box>
                    )}
                </Box>
            </Box>
        ),
        [navigate, readOnly, t],
    );

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: readOnly ? 0 : 1 }}>
                <UtmsSearchTextField globalFilter={globalFilter} setGlobalFilter={setGlobalFilter} />
                <Box sx={{ maxHeight: 300, overflow: 'auto' }} onScroll={handleScroll}>
                    {defectsView.map((defect: Defect) =>
                        readOnly ? (
                            <Box key={defect.pk} sx={{ p: '6px 8px' }}>
                                {defectItem(defect)}
                            </Box>
                        ) : (
                            <MenuItem key={defect.pk} onClick={handleSelectDefect(defect)}>
                                {defectItem(defect)}
                            </MenuItem>
                        ),
                    )}
                    {isLoading &&
                        [...Array(SKELETONS_COUNT)].map((_, i) => (
                            <MenuItem key={i}>
                                <Skeleton animation="wave" variant="text" width="100%" />
                            </MenuItem>
                        ))}
                    {getDefectsForSelectorStatus === RequestStatusEnum.Success &&
                        !defectsForSelector.length &&
                        defects?.length && (
                            <HelperText sx={{ fontSize: '0.86rem' }}>{t('no_record_to_display')}...</HelperText>
                        )}
                </Box>
            </Box>

            {getDefectsForSelectorStatus === RequestStatusEnum.Error && (
                <ModalErrorContainer
                    object={t('defects_r')}
                    action={t('fetching')}
                    resetActionCallback={getDefectsForSelectorReset}
                    errorSelector={selectGetDefectsForSelectorError}
                />
            )}
        </>
    );
};

export default DefectSelectorMenu;
